import { useEffect, forwardRef, useImperativeHandle, useState } from 'react';
import { Stack, Typography } from '@mui/material';

import { MODALS_STORE, TASKS_MODAL_ID } from 'constants/ui/modal';
import { useLocalization } from 'localization';
import { TaskStatus } from 'api';
import { colors } from 'theme/mui/colors';
import { TextAreaInputField } from 'components/v2/input/TextAreaInputField';
import { InputSelectField, SelectOption } from 'components/v2/input/InputSelectField';
import RefreshButton from 'components/v2/button/RefreshButton';
import { CheckboxField } from 'components/v2/checkbox/CheckboxField';
import { InputField } from 'components/v2/input/InputField';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { taskAITextInputSchema } from 'components/v2/division/form/schema/tasksSchema';
import { useContactPersons, usePropertyTypes } from 'modules/orders/hooks';
import { useContactPersonsByCaseId } from 'modules/cases/hooks';
import { useDepartmentMembers } from 'modules/branches/hooks';
import Questionnaire from 'modules/v2/guest/Questionnaire';

export interface AITextInputFormData {
  importantElement: string;
  property: {
    type: string;
  };
  responsibleAgent: string;
  contactPerson?: string;
  contactPersonName?: string;
  email?: string | null;
  isQuestionnaire: boolean;
}

export type DetailFormProps = {
  initialData: AITextInputFormData | null;
  onQuestionnaireChange: (isQuestionnaire: boolean) => void;
  isSendEmailAvailable?: boolean;
  onSubmit: (data: AITextInputFormData) => void;
};

export type SubmitFormRef = {
  submit: () => Promise<boolean>;
};

const DetailForm = forwardRef<SubmitFormRef, DetailFormProps>(
  ({ initialData, isSendEmailAvailable = false, onQuestionnaireChange, onSubmit }, ref) => {
    const { t } = useLocalization();

    const { getModalData } = MODALS_STORE.tasks();

    const caseId = getModalData(TASKS_MODAL_ID.AITextInput)?.task?.caseId ?? '';
    const branchId = getModalData(TASKS_MODAL_ID.AITextInput)?.task?.branchId ?? '';
    const taskStatus = getModalData(TASKS_MODAL_ID.AITextInput)?.task?.status;

    const { areDepartmentMembersLoading, departmentMembers, isGettingMembersError, refetchDepartments } =
      useDepartmentMembers(branchId);
    const { propertyTypeOptions, refetch, isError } = usePropertyTypes();
    const { contactPersonTypeOptions, isLoading, isContactPersonError, onRefetchContactPerson } = useContactPersons();
    const { data } = useContactPersonsByCaseId(caseId);

    const contactPersonType = contactPersonTypeOptions?.find(option => option.value === data?.[0]?.type);

    const responsibleAgentsOptions = departmentMembers.map(({ email, fullName }) => ({
      value: email,
      label: fullName,
    }));

    const methods = useForm({
      values: initialData ?? {
        importantElement: '',
        contactPerson: contactPersonType?.value,
        contactPersonName: data?.[0]?.name,
        isQuestionnaire: false,
        responsibleAgent: '',
        property: {
          type: '',
        },
        email: data?.[0]?.email,
      },
      resolver: yupResolver(taskAITextInputSchema()),
    });

    const { control, watch, handleSubmit } = methods;
    const isQuestionnaire = watch('isQuestionnaire');

    useEffect(() => {
      onQuestionnaireChange(isQuestionnaire);
    }, [isQuestionnaire]);

    useImperativeHandle(ref, () => ({
      submit: async () => {
        let validationPassed = false;
        await handleSubmit(
          data => {
            validationPassed = true;
            onSubmit(data);
          },
          () => {
            validationPassed = false;
          }
        )();
        return validationPassed;
      },
    }));

    return (
      <FormProvider {...methods}>
        <Stack gap={2}>
          <TextAreaInputField
            rows={3}
            required
            label="modals.textInput.inputArea.title"
            name="importantElement"
            control={control}
            disabled={taskStatus === TaskStatus.Complete}
          />
          <InputSelectField
            name="property.type"
            control={control}
            options={propertyTypeOptions}
            label="common.terms.propertyType"
            menuHeight={300}
            required
            disabled={taskStatus === TaskStatus.Complete}
          />
          {isError && (
            <Stack direction="row" alignItems="center">
              <Typography variant="h6" sx={{ color: colors.red[100] }}>
                {t('common.messages.fetchingError')}
              </Typography>
              <RefreshButton onRefetch={refetch} />
            </Stack>
          )}

          <InputSelectField
            name="responsibleAgent"
            control={control}
            options={responsibleAgentsOptions}
            label="common.terms.responsibleAgent"
            menuHeight={300}
            required
            loading={areDepartmentMembersLoading}
            disabled={taskStatus === TaskStatus.Complete}
          />
          {isGettingMembersError && (
            <Stack direction="row" alignItems="center">
              <Typography variant="h6" sx={{ color: colors.red[100] }}>
                {t('common.messages.fetchingError')}
              </Typography>
              <RefreshButton onRefetch={refetchDepartments} />
            </Stack>
          )}
          {isSendEmailAvailable && (
            <CheckboxField
              control={control}
              disabled={taskStatus === TaskStatus.Complete}
              label="common.terms.questionnaire"
              name="isQuestionnaire"
            />
          )}
          {isQuestionnaire && isSendEmailAvailable && (
            <>
              <InputSelectField
                loading={isLoading}
                options={(contactPersonTypeOptions as SelectOption[]) ?? []}
                control={control}
                label="common.terms.contactPerson"
                required
                name="contactPerson"
              />
              {isContactPersonError && (
                <Stack direction="row" alignItems="center">
                  <Typography variant="h6" sx={{ color: colors.red[100] }}>
                    {t('common.messages.fetchingError')}
                  </Typography>
                  <RefreshButton onRefetch={onRefetchContactPerson} />
                </Stack>
              )}
              <InputField control={control} label="common.terms.name" required name="contactPersonName" />
              <InputField control={control} label="common.terms.email" required name="email" />
            </>
          )}
        </Stack>
      </FormProvider>
    );
  }
);

export type QuestionnaireFormProps = {
  setIsSubmitting?: (isSubmitting: boolean) => void;
};

const QuestionnaireForm = forwardRef<SubmitFormRef, QuestionnaireFormProps>(({ setIsSubmitting }, ref) => {
  const { t } = useLocalization();
  const { getModalData } = MODALS_STORE.tasks();
  const taskId = getModalData(TASKS_MODAL_ID.AITextInput)?.task?.id;
  const [isError, setIsError] = useState(false);

  return isError ? (
    <Typography>{t('users.pages.questionnaire.notFound')}</Typography>
  ) : (
    <Questionnaire ref={ref} taskId={taskId} onError={() => setIsError(true)} setIsSubmitting={setIsSubmitting} />
  );
});

type StepConfig =
  | { titleKey: string; renderContent: (props: DetailFormProps) => React.ReactElement }
  | {
      titleKey: string;
      renderContent: (_: QuestionnaireFormProps, ref: React.Ref<SubmitFormRef>) => React.ReactElement;
    };

const aiTextInputModalContent: StepConfig[] = [
  { titleKey: 'modals.aiTextDetails.title', renderContent: (props: DetailFormProps) => <DetailForm {...props} /> },
  {
    titleKey: 'modals.aiTextDetails.questionnaire.title',
    renderContent: (props: QuestionnaireFormProps) => <QuestionnaireForm {...props} />,
  },
];

export default aiTextInputModalContent;
